/**
 * FeatureGate - Conditionally renders children based on feature flag state.
 * Used to guard pages/sections that can be disabled per-organization.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagsSafe, type FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { ShieldOff } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { TierLockedScreen } from './TierLockedScreen';

interface FeatureGateProps {
  flag: keyof FeatureFlags;
  children: ReactNode;
  /** Optional custom fallback. Defaults to a "feature disabled" page. */
  fallback?: ReactNode;
}

/**
 * `showBackLink={false}` where `/dashboard` is not a way out of the screen —
 * a deal tab inside Bitrix24, which has no portal route to go back to.
 */
export function FeatureDisabledFallback({
  showBackLink = true,
}: {
  showBackLink?: boolean;
}) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="rounded-full bg-bg-elevated p-4">
        <ShieldOff className="h-10 w-10 text-fg-muted" />
      </div>
      <div>
        <h2 className="text-base font-medium text-fg">
          {t('featureGate.title', 'Feature Not Available')}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          {t(
            'featureGate.description',
            'This feature is disabled for your organization. Contact your administrator for access.'
          )}
        </p>
      </div>
      {showBackLink && (
        <Link
          to="/dashboard"
          className="mt-2 rounded-button bg-primary px-4 py-2 text-sm font-medium text-bg transition-all hover:bg-primary-hover"
        >
          {t('featureGate.cta', 'Go to Overview')}
        </Link>
      )}
    </div>
  );
}

export function FeatureGate({ flag, children, fallback }: FeatureGateProps) {
  // Safe variant: embedded routes (and tests) render dashboards that use
  // FeatureGate without mounting FeatureFlagsProvider — falling back to
  // all-flags-disabled is correct there and avoids a hard crash.
  const { isEnabled, isReady, gate } = useFeatureFlagsSafe();

  // Avoid FOUC: before flags arrive we can't tell "disabled" from "still loading",
  // so render nothing rather than flashing "Feature Not Available".
  if (!isReady) {
    return null;
  }

  if (!isEnabled(flag)) {
    const verdict = gate(flag);
    // `loading`: the flag is off only by the localStorage seed, the server has
    // not said why yet. Either screen would be a guess — and the wrong one
    // flashes on every cold load of a locked page — so, like before ready,
    // nothing is drawn until the verdict arrives.
    if (verdict === 'loading') {
      return null;
    }
    // The plan is below the feature's floor (`tier_not_eligible`, #1897): the
    // screen names the plan and shows what the feature gives, instead of the
    // «ask your administrator» of `feature_disabled` — there the plan is fine
    // and an administrator holds the switch. A passed fallback is the caller's
    // own answer to every «not on» state and wins — `null` included: a caller
    // that passes `fallback={null}` asks for nothing, not for the default screen.
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    return verdict === 'tier_not_eligible' ? (
      <TierLockedScreen flag={flag} showBackLink />
    ) : (
      <FeatureDisabledFallback />
    );
  }

  return <>{children}</>;
}
